import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { collection, collectionData, doc, Firestore, getDoc, limit, orderBy, query, where } from '@angular/fire/firestore'
import { Subscription } from 'rxjs'
import { Client, FirestoreStructure, PayGateAttempt } from '@parkupp/core'

const RECENT_LIMIT = 300
/** A single client's history isn't capped at the recent 300, so older attempts show up too. */
const CLIENT_LIMIT = 1000

/**
 * Audit trail of every PayGate interaction — tokenisations, authorisations, settlements
 * and voids. Built to answer "is anything failing, and where?" without digging through
 * Slack history or cloud function logs.
 */
@Component({
    selector: 'app-admin-paygate-attempts',
    templateUrl: './admin-paygate-attempts.component.html',
    styleUrls: ['./admin-paygate-attempts.component.scss'],
})
export class AdminPaygateAttemptsComponent implements OnInit, OnDestroy {
    attempts: PayGateAttempt[] = []
    loading = true

    filterType = ''
    filterSource = ''
    failuresOnly = false
    searchText = ''
    /** When set, the page queries this client's attempts directly instead of the newest 300. */
    filterClient = ''

    /** Client display names by key, filled in as attempts arrive. */
    clientNames: Record<string, string> = {}
    private clientLookups = new Set<string>()

    expanded: Record<string, boolean> = {}

    private sub: Subscription | null = null

    /** Clients seen in the loaded attempts, for the filter dropdown. */
    clientOptions: { key: string; count: number }[] = []

    constructor(
        private firestore: Firestore,
        private route: ActivatedRoute,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.filterClient = this.route.snapshot.queryParamMap.get('client') || ''
        this.load()
    }

    /** Re-queries Firestore: the newest attempts overall, or every attempt for one client. */
    load(): void {
        this.sub?.unsubscribe()
        this.loading = true
        const attemptsRef = collection(this.firestore, 'paygateAttempts')
        // No orderBy on the client query so it runs off the single-field index; sorted below instead.
        const ref = this.filterClient
            ? query(attemptsRef, where('clientKey', '==', this.filterClient), limit(CLIENT_LIMIT))
            : query(attemptsRef, orderBy('createdAt', 'desc'), limit(RECENT_LIMIT))
        this.sub = collectionData(ref, { idField: '$key' }).subscribe((docs: any[]) => {
            this.attempts = docs
                .map((d) => new PayGateAttempt(d))
                .sort((a, b) => this.toDate(b.createdAt).getTime() - this.toDate(a.createdAt).getTime())
            if (!this.filterClient) this.clientOptions = this.buildClientOptions()
            this.resolveClientNames()
            this.loading = false
        })
    }

    selectClient(clientKey: string): void {
        this.filterClient = clientKey || ''
        this.router.navigate([], { relativeTo: this.route, queryParams: { client: this.filterClient || null }, replaceUrl: true })
        this.load()
    }

    private buildClientOptions(): { key: string; count: number }[] {
        const counts = new Map<string, number>()
        this.attempts.forEach((a) => a.clientKey && counts.set(a.clientKey, (counts.get(a.clientKey) || 0) + 1))
        return [...counts.entries()].map(([key, count]) => ({ key, count }))
    }

    /** Options sorted by name; re-sorted as names resolve. */
    get sortedClientOptions(): { key: string; count: number }[] {
        return [...this.clientOptions].sort((a, b) => this.clientName(a.key).localeCompare(this.clientName(b.key)))
    }

    /** A deep-linked client may not be among the recent attempts, so it needs its own option. */
    hasClientOption(key: string): boolean {
        return this.clientOptions.some((c) => c.key === key)
    }

    private resolveClientNames(): void {
        const keys = new Set<string>(this.attempts.map((a) => a.clientKey).filter(Boolean) as string[])
        if (this.filterClient) keys.add(this.filterClient)
        keys.forEach(async (key) => {
            if (this.clientLookups.has(key)) return
            this.clientLookups.add(key)
            try {
                const snap = await getDoc(doc(this.firestore, `${FirestoreStructure.CLIENTS}/${key}`))
                if (!snap.exists()) return
                const client = new Client(snap.data() as any, key)
                const name = (client.companyName || [client.firstName, client.lastName].filter(Boolean).join(' ')).trim()
                if (name) this.clientNames[key] = name
            } catch (e) {
                // Leave the key showing; a missing name shouldn't break the audit view.
            }
        })
    }

    clientName(key?: string): string {
        if (!key) return ''
        return this.clientNames[key] || key
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe()
    }

    get filtered(): PayGateAttempt[] {
        const needle = this.searchText.trim().toLowerCase()
        return this.attempts.filter((a) => {
            if (this.failuresOnly && a.success) return false
            if (this.filterType && a.type !== this.filterType) return false
            if (this.filterSource && a.source !== this.filterSource) return false
            if (!needle) return true
            return [a.clientKey, a.clientKey && this.clientNames[a.clientKey], a.subscriptionKey, a.transactionId, a.vaultId, a.payRequestId, a.maskedCard, a.resultDescription]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(needle))
        })
    }

    get failureCount(): number {
        return this.attempts.filter((a) => !a.success).length
    }

    /** Failures in the last 24h — the number worth reacting to. */
    get recentFailureCount(): number {
        const cutoff = Date.now() - 24 * 60 * 60 * 1000
        return this.attempts.filter((a) => !a.success && this.toDate(a.createdAt).getTime() > cutoff).length
    }

    toDate(value: any): Date {
        if (!value) return new Date(0)
        if (value.toDate) return value.toDate()
        if (value.seconds !== undefined) return new Date(value.seconds * 1000)
        if (value._seconds !== undefined) return new Date(value._seconds * 1000)
        return new Date(value)
    }

    badgeClass(attempt: PayGateAttempt): string {
        if (!attempt.success) return 'bg-danger'
        return attempt.type === 'void' ? 'bg-secondary' : 'bg-success'
    }

    outcomeClass(attempt: PayGateAttempt): string {
        switch (attempt.outcomeSource) {
            case 'notify': return 'bg-primary'
            case 'redirect': return 'bg-info text-dark'
            case 'pending': return 'bg-warning text-dark'
            case 'query': return 'bg-light text-dark border'
            default: return 'bg-light text-dark border'
        }
    }

    toggle(key?: string): void {
        if (!key) return
        this.expanded[key] = !this.expanded[key]
    }

    asAmount(cents?: number): string {
        return cents === undefined || cents === null ? '—' : `R${(cents / 100).toFixed(2)}`
    }
}
